import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Hash, FileText, Layers, XCircle, Eye, Download, Pin, PinOff, ClipboardList, ChevronRight } from "lucide-react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { format } from "date-fns"
import { cn } from "@/lib/utils"
import { InfoTooltip } from "../shared/InfoTooltip"
import { ExportDialog, ExportButton, type ExportColumn } from "../shared/ExportDialog"
import { CHART_CONFIG } from "../../utils/constants"
import { getDisplayName, getInitials, exportToCSV } from "../../utils/formatting"
import type { RawContact, RawMessage } from "../types"
import type { PinnedVarConfig, PinnedVarChartType, PinnedVarSortOrder } from "../../hooks/useVariablesAnalytics"
import { DEFAULT_PINNED_CONFIG } from "../../hooks/useVariablesAnalytics"

// Objects/arrays (repeating-group fields) export as JSON so the cell stays parseable.
function exportVarValue(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'object') {
    try { return JSON.stringify(v) } catch { return '' }
  }
  return String(v)
}

interface VarValueBreakdown {
  value: string
  count: number
  color: string
}

interface AllVarKeys {
  contact: string[]
  message: string[]
  form: string[]
}

export interface FormFieldMeta {
  title: string
  type?: string
  optionLabels?: Record<string, string>
  formId?: string
  formTitle?: string
}

interface VariablesTabProps {
  filteredContacts: RawContact[]
  filteredMessages: RawMessage[]
  allVarKeys: AllVarKeys
  selectedVarKey: string | null
  onSelectVarKey: (key: string) => void
  varValueBreakdown: VarValueBreakdown[]
  selectedVarValue: string | null
  onSelectVarValue: (value: string | null) => void
  contactsWithVarValue: RawContact[]
  onOpenInHITL: (contact: RawContact) => void
  pinnedVarKeys?: string[]
  pinnedVarConfigs?: Record<string, PinnedVarConfig>
  onTogglePin?: (key: string) => void
  onUpdatePinnedConfig?: (key: string, updates: Partial<PinnedVarConfig>) => void
  formFieldMeta?: Record<string, FormFieldMeta>
}

export function VariablesTab({
  filteredContacts,
  filteredMessages,
  allVarKeys,
  selectedVarKey,
  onSelectVarKey,
  varValueBreakdown,
  selectedVarValue,
  onSelectVarValue,
  contactsWithVarValue,
  onOpenInHITL,
  pinnedVarKeys = [],
  pinnedVarConfigs = {},
  onTogglePin,
  onUpdatePinnedConfig,
  formFieldMeta = {},
}: VariablesTabProps) {
  const [contactExportOpen, setContactExportOpen] = useState(false)
  const [messageExportOpen, setMessageExportOpen] = useState(false)
  // Form pins from before the Forms tab existed no longer show on Overview
  const overviewPins = pinnedVarKeys.filter(k => !k.startsWith("form."))

  // Which form's fields are currently expanded in the Form Variables drill-down.
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null)

  // For form.* keys, show the field's human title instead of the raw key.
  const displayVarName = (key: string): string => {
    const meta = formFieldMeta[key]
    if (meta?.title) return meta.title
    if (key.startsWith("form.")) return key.slice("form.".length)
    return key
  }

  // Map a stored option value to its label for list/multiselect form fields.
  const displayVarValue = (key: string, value: string): string => {
    const labels = formFieldMeta[key]?.optionLabels
    return (labels && labels[value]) || value
  }

  // A single selectable variable row (select button + pin toggle).
  const renderVarRow = (key: string, prefix: string, icon: React.ReactNode) => (
    <div key={`${prefix}-${key}`} className="flex items-center gap-1 group min-w-0">
      <Button
        variant={selectedVarKey === key ? 'default' : 'ghost'}
        size="sm"
        className="flex-1 min-w-0 justify-start text-sm"
        onClick={() => onSelectVarKey(key)}
        title={displayVarName(key)}
      >
        <span className="mr-2 shrink-0">{icon}</span>
        <span className="truncate">{displayVarName(key)}</span>
      </Button>
      {/* Form fields aren't pinned: every field shows on the Forms tab */}
      {onTogglePin && !key.startsWith("form.") && (
        <Button
          variant="ghost"
          size="sm"
          className={cn("h-7 w-7 p-0 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity", pinnedVarKeys.includes(key) && "opacity-100 text-primary")}
          onClick={() => onTogglePin(key)}
          title={pinnedVarKeys.includes(key) ? 'Remove from overview' : 'Pin to overview'}
        >
          {pinnedVarKeys.includes(key) ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
        </Button>
      )}
    </div>
  )

  // Shared collapsible renderer for a labelled group of selectable variable keys.
  const renderVarGroup = (label: string, prefix: string, keys: string[], icon: React.ReactNode, headerIcon: React.ReactNode) => (
    <Collapsible defaultOpen>
      <CollapsibleTrigger className="flex items-center gap-1 w-full [&[data-state=open]>svg]:rotate-90">
        <ChevronRight className="h-3 w-3 shrink-0 transition-transform text-muted-foreground" />
        <Label className="text-xs text-muted-foreground uppercase flex items-center gap-1 cursor-pointer flex-1 min-w-0">
          {headerIcon} <span className="truncate">{label}</span>
        </Label>
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">{keys.length}</Badge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ScrollArea className="h-[150px] mt-2">
          <div className="space-y-1">
            {keys.length === 0 && (
              <p className="text-xs text-muted-foreground italic px-1 py-2">None in this range.</p>
            )}
            {keys.map(key => renderVarRow(key, prefix, icon))}
          </div>
        </ScrollArea>
      </CollapsibleContent>
    </Collapsible>
  )

  // Form variables grouped by their owning form, so fields are easy to find.
  // Built ONLY from fields defined on the team's forms (via formFieldMeta), so
  // the list mirrors the real forms. Values with no matching field definition
  // (orphans from deleted/test forms) are intentionally excluded. `hasData`
  // marks fields that actually have captured values in the current range.
  const formGroups = useMemo(() => {
    type Group = { formId?: string; formTitle: string; keys: string[]; hasData: Set<string> }
    const groups = new Map<string, Group>()
    const populated = new Set(allVarKeys.form)

    for (const [key, meta] of Object.entries(formFieldMeta)) {
      const groupId = meta.formId ?? "__ungrouped__"
      const formTitle = meta.formTitle ?? "Form Fields"
      if (!groups.has(groupId)) groups.set(groupId, { formId: meta.formId, formTitle, keys: [], hasData: new Set() })
      const g = groups.get(groupId)!
      g.keys.push(key)
      if (populated.has(key)) g.hasData.add(key)
    }

    return Array.from(groups.values())
      .map(g => ({ ...g, keys: g.keys.sort((a, b) => displayVarName(a).localeCompare(displayVarName(b))) }))
      .sort((a, b) => a.formTitle.localeCompare(b.formTitle))
  }, [allVarKeys.form, formFieldMeta])

  // Drill-down: Form Variables ▸ → pick a form ▸ → pick a variable on that form.
  // Only one form's fields are shown at a time.
  const renderFormVariables = () => (
    <Collapsible defaultOpen>
      <CollapsibleTrigger className="flex items-center gap-1 w-full [&[data-state=open]>svg]:rotate-90">
        <ChevronRight className="h-3 w-3 shrink-0 transition-transform text-muted-foreground" />
        <Label className="text-xs text-muted-foreground uppercase flex items-center gap-1 cursor-pointer">
          <ClipboardList className="h-2.5 w-2.5" /> Form Variables
        </Label>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 space-y-1">
          {formGroups.map((group, i) => {
            const isOpen = selectedFormId === (group.formId ?? `__idx_${i}`)
            const groupId = group.formId ?? `__idx_${i}`
            return (
              <div key={groupId} className="rounded-md border">
                <button
                  type="button"
                  className="flex items-center gap-1.5 w-full rounded-md px-2 py-1.5 text-xs font-medium hover:bg-muted/50"
                  onClick={() => setSelectedFormId(isOpen ? null : groupId)}
                >
                  <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 transition-transform text-muted-foreground", isOpen && "rotate-90")} />
                  <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="truncate flex-1 text-left">{group.formTitle}</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">{group.keys.length}</Badge>
                </button>
                {isOpen && (
                  <div className="space-y-1 px-2 pb-2 pt-1">
                    {group.keys.length === 0 && (
                      <p className="text-xs text-muted-foreground italic px-1 py-1.5">This form has no fields.</p>
                    )}
                    {group.keys.map(key => (
                      <div key={`form-wrap-${key}`} className={cn("min-w-0", !group.hasData.has(key) && "opacity-50")}>
                        {renderVarRow(key, 'form', <Hash className="h-3 w-3" />)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )

  const { contactVarColumns, contactVarData } = useMemo(() => {
    const varKeys = new Set<string>()
    filteredContacts.forEach(c => {
      if (c.vars) Object.keys(c.vars).forEach(k => varKeys.add(k))
    })

    const columns: ExportColumn[] = [
      { key: 'id', label: 'Contact ID' },
      { key: 'phone', label: 'Phone Number' },
      { key: 'name', label: 'Name' },
      ...Array.from(varKeys).sort().map(k => ({ key: k, label: k })),
    ]

    const data = filteredContacts.map(c => ({
      id: c.id,
      phone: c.phone_number || '',
      name: c.name || '',
      ...Object.fromEntries(
        Object.entries(c.vars || {}).map(([k, v]) => [k, exportVarValue(v)])
      ),
    }))

    return { contactVarColumns: columns, contactVarData: data }
  }, [filteredContacts])

  const { messageVarColumns, messageVarData } = useMemo(() => {
    const varKeys = new Set<string>()
    filteredMessages.forEach(m => {
      if (m.vars) Object.keys(m.vars).forEach(k => varKeys.add(k))
    })

    const columns: ExportColumn[] = [
      { key: 'id', label: 'Message ID' },
      { key: 'contact_id', label: 'Contact ID' },
      { key: 'direction', label: 'Direction' },
      { key: 'time', label: 'Time' },
      ...Array.from(varKeys).sort().map(k => ({ key: k, label: k })),
    ]

    const data = filteredMessages.map(m => ({
      id: m.id,
      contact_id: m.contact_id,
      direction: m.direction,
      time: format(new Date(m.time_created * 1000), 'yyyy-MM-dd HH:mm:ss'),
      ...Object.fromEntries(
        Object.entries(m.vars || {}).map(([k, v]) => [k, exportVarValue(v)])
      ),
    }))

    return { messageVarColumns: columns, messageVarData: data }
  }, [filteredMessages])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Variable Selector */}
        <Card className="lg:col-span-1 min-w-0">
          <CardHeader>
            <CardTitle className="text-base">
              Available Variables
              <InfoTooltip text="Custom variables stored on contacts and messages. Click a variable to see its value distribution." />
            </CardTitle>
            <CardDescription>Select a variable to see value distribution</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pinned Variables with config */}
            {overviewPins.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase flex items-center gap-1">
                  <Pin className="h-2.5 w-2.5" /> Pinned to Overview
                </Label>
                {overviewPins.map(key => {
                  const cfg = pinnedVarConfigs[key] ?? DEFAULT_PINNED_CONFIG
                  return (
                    <div key={key} className="border rounded-md px-2.5 py-1.5 flex items-center gap-1.5 bg-muted/30">
                      <span className="text-xs font-medium truncate flex-1 min-w-0">{key}</span>

                      <Select
                        value={cfg.chartType}
                        onValueChange={v => onUpdatePinnedConfig?.(key, { chartType: v as PinnedVarChartType })}
                      >
                        <SelectTrigger className="h-6 w-[62px] text-[10px] px-1.5 shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bar" className="text-xs">Bar</SelectItem>
                          <SelectItem value="pie" className="text-xs">Pie</SelectItem>
                          <SelectItem value="number" className="text-xs">List</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select
                        value={cfg.sortOrder ?? 'count_desc'}
                        onValueChange={v => onUpdatePinnedConfig?.(key, { sortOrder: v as PinnedVarSortOrder })}
                      >
                        <SelectTrigger className="h-6 w-[84px] text-[10px] px-1.5 shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="count_desc" className="text-xs">Most common</SelectItem>
                          <SelectItem value="count_asc" className="text-xs">Least common</SelectItem>
                          <SelectItem value="alpha_asc" className="text-xs">A → Z</SelectItem>
                          <SelectItem value="alpha_desc" className="text-xs">Z → A</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select
                        value={cfg.hideNull ? 'hide' : 'show'}
                        onValueChange={v => onUpdatePinnedConfig?.(key, { hideNull: v === 'hide' })}
                      >
                        <SelectTrigger className="h-6 w-[70px] text-[10px] px-1.5 shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="show" className="text-xs">All values</SelectItem>
                          <SelectItem value="hide" className="text-xs">Hide empty</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => onTogglePin?.(key)}
                        title="Unpin"
                      >
                        <PinOff className="h-3 w-3" />
                      </Button>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Form Variables — drill down by form, then pick a field */}
            {formGroups.length > 0 && renderFormVariables()}

            {/* Contact Variables */}
            {renderVarGroup('Contact Variables', 'contact', allVarKeys.contact, <Hash className="h-3 w-3" />, <Hash className="h-2.5 w-2.5" />)}

            {/* Message Variables */}
            {renderVarGroup('Message Variables', 'message', allVarKeys.message, <FileText className="h-3 w-3" />, <FileText className="h-2.5 w-2.5" />)}
          </CardContent>
        </Card>

        {/* Variable Analysis */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">
              {selectedVarKey ? `Distribution: ${displayVarName(selectedVarKey)}` : 'Select a Variable'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedVarKey ? (
              <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                <Layers className="h-12 w-12 mb-4 opacity-30" />
                <p>Select a variable from the list to see its distribution</p>
              </div>
            ) : varValueBreakdown.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                <XCircle className="h-12 w-12 mb-4 opacity-30" />
                <p>No values found for this variable</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Bar Chart — `label` is the display text (option labels for form
                    fields); `value` stays the raw key used for click-through. */}
                <ChartContainer config={CHART_CONFIG} className="h-[250px] w-full">
                  <BarChart
                    data={varValueBreakdown.slice(0, 10).map(d => ({ ...d, label: displayVarValue(selectedVarKey, d.value) }))}
                    layout="vertical"
                    onClick={(data: any) => {
                      if (data && data.activePayload && data.activePayload[0]) {
                        onSelectVarValue(data.activePayload[0].payload.value)
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" className="text-xs" />
                    <YAxis dataKey="label" type="category" width={120} className="text-xs" tick={{ fontSize: 11 }} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} cursor="pointer">
                      {varValueBreakdown.slice(0, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ChartContainer>

                {/* Value Table */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Value</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                      <TableHead className="text-right">Percentage</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {varValueBreakdown.map(({ value, count, color }) => {
                      const total = varValueBreakdown.reduce((sum, v) => sum + v.count, 0)
                      return (
                        <TableRow
                          key={value}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50 transition-colors",
                            selectedVarValue === value && "bg-muted"
                          )}
                          onClick={() => onSelectVarValue(value)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
                              <span className="font-mono text-sm">{displayVarValue(selectedVarKey, value)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">{count}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {((count / total) * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation()
                                onSelectVarValue(value)
                              }}
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contacts with Selected Variable Value */}
      {selectedVarKey && selectedVarValue && contactsWithVarValue.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>
                Contacts with {displayVarName(selectedVarKey)} = "{displayVarValue(selectedVarKey, selectedVarValue)}"
              </span>
              <Badge variant="secondary">{contactsWithVarValue.length} contacts</Badge>
            </CardTitle>
            <CardDescription>
              Click a contact to view their conversation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {contactsWithVarValue.map((contact) => (
                  <Card
                    key={contact.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => onOpenInHITL(contact)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full text-white text-sm font-bold bg-linear-to-br from-blue-500 to-blue-700 shrink-0">
                          {getInitials(contact)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {getDisplayName(contact)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {contact.phone_number}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {contact.message_count || 0} messages
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Export Variables */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Export Variable Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <ExportButton onClick={() => setContactExportOpen(true)} label="Export Contact Variables" />
            <ExportButton onClick={() => setMessageExportOpen(true)} label="Export Message Variables" />
          </div>
        </CardContent>
      </Card>

      <ExportDialog
        open={contactExportOpen}
        onOpenChange={setContactExportOpen}
        title="Export Contact Variables"
        description={`Export ${filteredContacts.length} contacts with their variables — choose which columns to include`}
        columns={contactVarColumns}
        data={contactVarData}
        filename="contact_variables"
      />

      <ExportDialog
        open={messageExportOpen}
        onOpenChange={setMessageExportOpen}
        title="Export Message Variables"
        description={`Export ${filteredMessages.length} messages with their variables — choose which columns to include`}
        columns={messageVarColumns}
        data={messageVarData}
        filename="message_variables"
      />
    </div>
  )
}
